import React from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { ChatRoomsRowFragment } from "generated/graphql";
import { useGlobalAlertDialog } from "hooks";
import moment from "moment";
import { Link } from "react-router";
import { ListChildComponentProps } from "react-window";
import { displayDateFormat } from "utils/const";
import { createdByToName, trimLongText } from "utils/helper";
import { ChatRoomFormDialog, ChatRoomFormDialogStatus } from "./chat-room-form-dialog";

type ChatRoomListRowProps = ListChildComponentProps & {
  chatRooms: ChatRoomsRowFragment[];
  chatRoomId: string;
  onEditChatRoom: (id: string, name: string) => Promise<void>;
  onDeleteChatRoom: (id: string) => void;
};

export const ChatRoomListRow: React.FC<ChatRoomListRowProps> = (props) => {
  const { chatRooms, chatRoomId, index, style, onEditChatRoom, onDeleteChatRoom } = props;
  const chatRoom = chatRooms[index];

  const { displayGlobalAlertDialog } = useGlobalAlertDialog();

  const lastUpdatedAt =
    chatRoom.ChatHistories.length > 0 && chatRoom.ChatHistories[0]?.createdAt
      ? moment(chatRoom.ChatHistories[0].createdAt)
      : moment(chatRoom.createdAt);

  const lastUpdateBy =
    chatRoom.ChatHistories.length > 0 && chatRoom.ChatHistories[0]?.createdBy
      ? chatRoom.ChatHistories[0].createdBy
      : chatRoom.createdBy;

  const handleSubmit = async (name: string) => {
    try {
      await onEditChatRoom(chatRoom.id, name);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleDeleteChatRoom = () => {
    onDeleteChatRoom(chatRoom.id);
  };

  return (
    <ListItem
      style={style}
      key={index}
      component="div"
      disablePadding
      secondaryAction={
        <div
          style={{
            margin: "10px 0",
          }}
        >
          <ChatRoomFormDialog
            onSubmit={handleSubmit}
            mode={ChatRoomFormDialogStatus.edit}
            defaultValue={chatRoom.name}
            buttonProps={{
              color: "primary",
              size: "small",
            }}
          />
          <IconButton
            size="small"
            onClick={() => {
              displayGlobalAlertDialog({
                title: `Delete Chat Room: ${chatRoom.name}`,
                content: "Are you sure you want to delete this chat room?",
                handleConfirm: handleDeleteChatRoom,
              });
            }}
          >
            <DeleteIcon></DeleteIcon>
          </IconButton>
        </div>
      }
    >
      <ListItemButton
        divider={true}
        selected={chatRoom.id === chatRoomId}
        component={Link}
        to={`/chatroom/${chatRoom.id}`}
      >
        <ListItemText
          primary={trimLongText(chatRoom.name, 25)}
          secondary={
            <React.Fragment>
              <Typography component="span" variant="body2" fontSize={10}>
                {createdByToName(lastUpdateBy)}
              </Typography>
              {lastUpdatedAt && (
                <Typography component="span" variant="body2" fontSize={10}>
                  {` - ${lastUpdatedAt.format(displayDateFormat)}`}
                </Typography>
              )}
            </React.Fragment>
          }
          slotProps={{
            primary: { fontSize: 20 },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};
